import { memo, useMemo } from 'react'
import { type Resident } from '../api/residentApi'

interface ResidentCardProps {
  resident: Resident
  onEdit?: (resident: Resident) => void
  onMoveOut?: (resident: Resident) => void
  onViewDetails?: (resident: Resident) => void
}

export const ResidentCard = memo<ResidentCardProps>(({
  resident,
  onEdit,
  onMoveOut,
  onViewDetails
}) => {
  // Memoized calculations
  const daysSober = useMemo(() => 
    resident.sobrietyDate 
      ? Math.floor((new Date().getTime() - new Date(resident.sobrietyDate).getTime()) / (1000 * 60 * 60 * 24))
      : null,
    [resident.sobrietyDate]
  );

  const daysInHouse = useMemo(() => 
    Math.floor((new Date().getTime() - new Date(resident.moveInDate).getTime()) / (1000 * 60 * 60 * 24)),
    [resident.moveInDate]
  );

  // Memoized accessibility labels
  const residentName = `${resident.firstName} ${resident.lastName}`;
  const statusLabel = `Resident status: ${resident.isActive ? 'Active' : 'Inactive'}`;

  return (
    <article 
      className="
        bg-white rounded-lg shadow-md border border-gray-200 p-4 sm:p-6 
        hover:shadow-lg transition-shadow duration-200
        focus-within:ring-2 focus-within:ring-indigo-500 focus-within:ring-offset-2
        high-contrast:border-2 high-contrast:border-gray-600
      "
      role="article"
      aria-labelledby={`resident-${resident.id}-name`}
    >
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-4">
        <div className="flex items-center space-x-3 min-w-0 flex-1">
          {resident.avatar ? (
            <img
              src={resident.avatar}
              alt=""
              className="w-12 h-12 sm:w-14 sm:h-14 rounded-full object-cover flex-shrink-0"
              role="img"
            />
          ) : (
            <div 
              className="
                w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-gray-300 flex items-center justify-center flex-shrink-0
                high-contrast:bg-gray-400 high-contrast:border high-contrast:border-gray-600
              "
              role="img"
              aria-label={`Avatar for ${residentName}`}
            >
              <span className="text-gray-600 font-medium text-sm sm:text-base high-contrast:text-gray-900">
                {resident.firstName[0]}{resident.lastName[0]}
              </span>
            </div>
          )}
          <div className="min-w-0 flex-1">
            <h3 
              id={`resident-${resident.id}-name`}
              className="text-lg sm:text-xl font-semibold text-gray-900 truncate"
            >
              {residentName}
            </h3>
            <p className="text-sm text-gray-600 truncate">{resident.house.name}</p>
          </div>
        </div>
        <div className="flex items-center justify-start sm:justify-end">
          <span 
            className={`
              inline-flex items-center px-2 py-1 rounded-full text-xs font-medium
              high-contrast:border high-contrast:border-gray-600
              ${resident.isActive 
                ? 'bg-green-100 text-green-800 high-contrast:bg-green-200 high-contrast:text-green-900' 
                : 'bg-gray-100 text-gray-800 high-contrast:bg-gray-200 high-contrast:text-gray-900'
              }
            `}
            aria-label={statusLabel}
          >
            {resident.isActive ? 'Active' : 'Inactive'}
          </span>
        </div>
      </div>

      {/* Enhanced responsive grid layout */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
        <div>
          <p className="text-sm text-gray-600 font-medium">Move-in Date</p>
          <p className="font-semibold text-gray-900">
            {new Date(resident.moveInDate).toLocaleDateString()}
          </p>
          <p className="text-xs text-gray-500">
            <span className="sr-only">Duration: </span>
            {daysInHouse} days
          </p>
        </div>
        {daysSober !== null && (
          <div>
            <p className="text-sm text-gray-600 font-medium">Sobriety</p>
            <p className="font-semibold text-gray-900">{daysSober} days</p>
            <p className="text-xs text-gray-500">
              Since {new Date(resident.sobrietyDate!).toLocaleDateString()}
            </p>
          </div>
        )}
      </div>

      {/* Room and Payment Information - Enhanced accessibility */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
        {resident.roomNumber && (
          <div>
            <p className="text-sm text-gray-600 font-medium">Room</p>
            <p className="font-semibold text-gray-900">{resident.roomNumber}</p>
          </div>
        )}
        {resident.paymentStatus && (
          <div>
            <p className="text-sm text-gray-600 font-medium">Payment Status</p>
            <span 
              className={`
                inline-flex items-center px-2 py-1 rounded-full text-xs font-medium
                high-contrast:border high-contrast:border-gray-600
                ${resident.paymentStatus === 'current' 
                  ? 'bg-green-100 text-green-800 high-contrast:bg-green-200 high-contrast:text-green-900'
                  : resident.paymentStatus === 'late'
                  ? 'bg-red-100 text-red-800 high-contrast:bg-red-200 high-contrast:text-red-900'
                  : 'bg-yellow-100 text-yellow-800 high-contrast:bg-yellow-200 high-contrast:text-yellow-900'
                }
              `}
              aria-label={`Payment status: ${resident.paymentStatus}`}
            >
              {resident.paymentStatus.charAt(0).toUpperCase() + resident.paymentStatus.slice(1)}
            </span>
          </div>
        )}
      </div>

      {/* Contact Information - Enhanced layout */}
      <div className="space-y-2 mb-4">
        {resident.email && (
          <div>
            <p className="text-sm text-gray-600 font-medium">Email</p>
            <a 
              href={`mailto:${resident.email}`}
              className="
                text-sm text-blue-600 hover:text-blue-800 transition-colors
                focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 rounded
                break-all
              "
              aria-label={`Send email to ${residentName}`}
            >
              {resident.email}
            </a>
          </div>
        )}

        {resident.phone && (
          <div>
            <p className="text-sm text-gray-600 font-medium">Phone</p>
            <a 
              href={`tel:${resident.phone}`}
              className="
                text-sm text-blue-600 hover:text-blue-800 transition-colors
                focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 rounded
              "
              aria-label={`Call ${residentName}`}
            >
              {resident.phone}
            </a>
          </div>
        )}
      </div>

      {/* Actions - Enhanced responsive design and accessibility */}
      <div className="flex flex-col sm:flex-row sm:justify-end gap-2 sm:gap-3 pt-4 border-t border-gray-200">
        {onViewDetails && (
          <button
            onClick={() => onViewDetails(resident)}
            className="
              px-4 py-2 text-sm text-blue-600 hover:text-blue-800 hover:bg-blue-50
              transition-colors duration-200 rounded-md
              focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
              min-h-[44px] sm:min-h-[36px]
              high-contrast:border high-contrast:border-blue-600
            "
            aria-label={`View details for ${residentName}`}
          >
            View Details
          </button>
        )}
        {onEdit && resident.isActive && (
          <button
            onClick={() => onEdit(resident)}
            className="
              px-4 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-50
              transition-colors duration-200 rounded-md
              focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2
              min-h-[44px] sm:min-h-[36px]
              high-contrast:border high-contrast:border-gray-600
            "
            aria-label={`Edit ${residentName}`}
          >
            Edit
          </button>
        )}
        {onMoveOut && resident.isActive && (
          <button
            onClick={() => {
              if (window.confirm(`Are you sure you want to move out ${residentName}? This action will mark them as inactive.`)) {
                onMoveOut(resident);
              }
            }}
            className="
              px-4 py-2 text-sm text-red-600 hover:text-red-800 hover:bg-red-50
              transition-colors duration-200 rounded-md
              focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2
              min-h-[44px] sm:min-h-[36px]
              high-contrast:border high-contrast:border-red-600
            "
            aria-label={`Move out ${residentName}`}
          >
            Move Out
          </button>
        )}
      </div>
    </article>
  )
});

ResidentCard.displayName = 'ResidentCard';